import get from 'lodash/get';

import createForm from '@evidentid/json-schema/createForm';
import { JsonSchemaArray, JsonSchemaEnum, JsonSchemaType } from '@evidentid/json-schema/interfaces/JsonSchema';
import { CustomProperty, EntityInput } from '@evidentid/tprm-portal-lib/models/dashboard';

import { removeNonStandardSchemaFormat } from '@/modules/decisioning-criteria/utils/removeNonStandardSchemaFormat';
import { getEnumLabel } from '@/utils/enum-labels/enumLabels';

function formatPhoneNumber(phoneNumber: string): string {
    const number = phoneNumber.replace(/[^\d]/g, '');
    if (number.length === 10) {
        return number.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
    } else if (number.length === 11) {
        return number.substr(1).replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
    }
    return number;
}

/** `worksheet` writes the CSV worksheet's forms ("Yes"/"No", "|"-separated lists); `downloadData` writes the
 * Download Data export's (true/false, JSON arrays), which also keeps a "|" inside a value. */
type CsvEncoding = 'worksheet' | 'downloadData';

function formatList(values: unknown[], encoding: CsvEncoding): string {
    if (encoding === 'downloadData') {
        return values.length > 0 ? JSON.stringify(values) : '';
    }
    return values.map((item) => (typeof item === 'object' ? JSON.stringify(item) : item)).join('|');
}

function formatCustomProperty(customProperty: CustomProperty, value: any, encoding: CsvEncoding): string {
    if (typeof value === 'boolean') {
        if (encoding === 'downloadData') {
            return String(value);
        }
        return value ? 'Yes' : 'No';
    } else if (Array.isArray(value)) {
        const schema = customProperty.schema as JsonSchemaArray<any>;
        if (encoding === 'downloadData') {
            const items = schema.items.type === JsonSchemaType.object
                ? createForm(removeNonStandardSchemaFormat(schema)).getValue(value, true)
                : value;
            return formatList(items, encoding);
        }
        if (schema.items.type !== JsonSchemaType.object) {
            const items = schema.items as unknown as JsonSchemaEnum;
            return items.enum
                ? value.map((item) => getEnumLabel(items, item)).join('|')
                : value.join('|');
        }
        const formValue = createForm(removeNonStandardSchemaFormat(schema)).getValue(value, true);
        return formValue.map((x: object) => JSON.stringify(x)).join('|');
    } else {
        return value?.toString() || '';
    }
}

function entityKeyToValue(
    key: string,
    entity: EntityInput,
    customProperties: CustomProperty[],
    encoding: CsvEncoding,
): string {
    if (key === 'contactPhoneNumber') {
        return formatPhoneNumber(entity.contactPhoneNumber || '');
    } else if (entity[key as keyof EntityInput] !== undefined) {
        const val = entity[key as keyof EntityInput];
        return Array.isArray(val) ? formatList(val, encoding) : val as string;
    } else if (entity.insuredFields && get(entity.insuredFields, key) !== undefined) {
        const customProperty = customProperties
            .find((customProperty) => customProperty.key === key) as CustomProperty;
        return formatCustomProperty(
            customProperty,
            // get because in case of objectProp column key in a format of objectName.propName
            get(entity.insuredFields, key),
            encoding,
        );
    } else if (entity.exceptions && entity.exceptions[key] !== undefined) {
        return entity.exceptions[key];
    } else {
        return '';
    }
}

function buildRows(
    columns: { headers: string[], keys: string[] },
    entities: EntityInput[],
    customProperties: CustomProperty[],
    encoding: CsvEncoding,
): string[][] {
    return [
        [ ...columns.headers.map((label) => label || '') ],
        ...entities.map((entity) =>
            columns.keys.map((key) => entityKeyToValue(key, entity, customProperties, encoding)),
        ),
    ];
}

export function buildEntitiesCsv(
    csvHeaders: string[],
    csvColumnKeys: string[],
    entities: EntityInput[],
    customProperties: CustomProperty[],
): string[][] {
    return buildRows({ headers: csvHeaders, keys: csvColumnKeys }, entities, customProperties, 'worksheet');
}

export function buildEntitiesDownloadDataCsv(
    csvHeaders: string[],
    csvColumnKeys: string[],
    entities: EntityInput[],
    customProperties: CustomProperty[],
): string[][] {
    return buildRows({ headers: csvHeaders, keys: csvColumnKeys }, entities, customProperties, 'downloadData');
}
